import { useMemo } from 'react';
import {
  AllocationTotals,
  AllocationWarning,
} from 'pages/development-areas/show/allocation-manager-types';

export const useAllocationWarnings = (
  // Consulted only for its length (see the guard below) — every other input comes
  // from `totals` and the scalars. Typed as the contract rather than as a concrete
  // line item so callers whose rows have a different shape need no unchecked cast,
  // and so widening this to read a field has to be a deliberate type change the
  // compiler then enforces on every caller.
  allocationManagerLineItems: readonly unknown[],
  totals: AllocationTotals | null,
  renderLateralLengthValues: boolean,
  unitPlatGrossAcres: number,
  wellPlatTotalLateralLength: number,
  totalGisAcreageSourced: number,
  totalGisLateralSourced: number
) => {
  return useMemo<AllocationWarning[]>(() => {
    if (!allocationManagerLineItems?.length) return [];
    if (!totals) return [];

    const warnings: AllocationWarning[] = [];

    // Helper function to add warnings
    const addWarning = (id: string, subtitle: string) => {
      warnings.push({
        id,
        title: 'GIS Validation Alert',
        subtitle,
      });
    };

    // 1. System Acreage Check
    const acreageGisThreshold = totalGisAcreageSourced * 0.05;
    if (
      Math.abs(unitPlatGrossAcres - totalGisAcreageSourced) >
      acreageGisThreshold
    ) {
      addWarning(
        'acreage-gis-value-breach',
        'Total Value (Sourced) exceeds 5% threshold compared to Total GIS Calc Value (Sourced)'
      );
    }

    // 2. Check if each tract's acreage value is within 5% of GIS calc value
    const acreageDifference = Math.abs(
      totals.acreageValue - totals.acreageGisValue
    );
    const acreageThreshold = totals.acreageGisValue * 0.05;

    if (acreageDifference > acreageThreshold) {
      addWarning(
        'acreage-value-total-breach',
        'Total Value (Sum) exceeds 5% threshold compared to Total GIS Calc Value (Sum)'
      );
    }

    // 3. Check if acreage factor values sum to unit plat gross acres & factor is 100%
    if (Math.abs(totals.acreageFactor - 1) > 0.0001) {
      addWarning(
        'acreage-factor-total-breach',
        'Total Value (Sum) does not equal Total Value (Sourced), Factor is not 100%'
      );
    }

    // Lateral Length Checks (if applicable)
    if (renderLateralLengthValues) {
      // 4. Check lateral GIS value against source
      const lateralGisThreshold = totalGisLateralSourced * 0.05;

      if (
        Math.abs(wellPlatTotalLateralLength - totalGisLateralSourced) >
        lateralGisThreshold
      ) {
        addWarning(
          'lateral-gis-value-breach',
          'Total Value (Sourced) exceeds 5% threshold compared to Total GIS Calc Value (Sourced)'
        );
      }

      // 5. Check if lateral value is within 3% of GIS calc value
      const lateralDifference = Math.abs(
        totals.lateralValue - totals.lateralGisValue
      );
      const lateralThreshold = totals.lateralGisValue * 0.03;

      if (lateralDifference > lateralThreshold) {
        addWarning(
          'lateral-value-total-breach',
          'Total Value (Sum) exceeds 3% threshold compared to Total GIS Calc Value (Sum)'
        );
      }

      // 6. Check if lateral factors sum to 100%
      if (Math.abs(totals.lateralFactor - 1) > 0.0001) {
        addWarning(
          'lateral-factor-total-breach',
          'Total Value (Sum) does not equal Total Value (Sourced), Factor is not 100%'
        );
      }
    }

    return warnings;
  }, [
    allocationManagerLineItems,
    renderLateralLengthValues,
    totals,
    unitPlatGrossAcres,
    wellPlatTotalLateralLength,
    totalGisAcreageSourced,
    totalGisLateralSourced,
  ]);
};
